import { Model } from '@mode-vegas/vegas';
import * as angular from 'angular';
import Backoff from 'backo';
import { Observable } from 'rxjs';
import { combineLatest, switchMap, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { DatasourceFacade } from '@mode/accounts/domain';
import {
  DefinitionResource,
  DeprecatedApiService,
  QueryResource,
  QueryRunResource,
  QueryRunResourceHelpers,
  ReportRunResource,
} from '@mode/legacy/data-access-model-js';
import { RUNNING_QUERY_STATES } from '@mode/shared/contract-common';
import { ToastService } from '@mode/shared/util-toast';
import {
  DefinitionUpdateStrategy,
  DefinitionUpdateStrategyTypes,
  SelectedDefinitionActionType,
  runDefinition,
  runDefinitionFulfilled,
  selectedDefinitionQueryResource,
} from '~/modules/definitions/domain';
import { LoadQueryRunView } from '../actions/report-run.actions';
import { StoreConnector } from '../common/store-connector';
import { ModeApp } from '../reducers/index';
import { reportContextSelector } from '../reducers/selectors';
import { FavIconService } from './favicon-service';

const minPollInterval = 50;
const maxPollInterval = 5000;
const pollFactor = 1.2;

export class DefinitionRunService {
  static $inject = [
    '$q',
    '$rootScope',
    '$log',
    '$timeout',
    '$window',
    'StoreService',
    'RxStoreService',
    'actions$',
    'DatasourceFacade',
    'LibraryDefinitionModel',
    'ToastService',
    'ReportRunModel',
    'QueryRunModel',
    'DataSourceModel',
    'ResultModel',
    'ModeCollection',
    'ApiService',
    'FavIconService',
  ];

  timeoutPromise: angular.IPromise<void>;
  cancelling = false;

  // The current/last report run that is/was run. Used for stopping the polling of a report run that's been replaced.
  currentRunToken: string;

  constructor(
    private $q: angular.IQService,
    private $root: angular.IRootScopeService,
    private $log: angular.ILogService,
    private $timeout: angular.ITimeoutService,
    private $window: angular.IWindowService,
    private StoreService: StoreConnector<ModeApp>,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private datasourceFacade: DatasourceFacade,
    private LibraryDefinitionModel: any,
    private alertService: ToastService,
    private ReportRunModel,
    private QueryRunModel: any,
    private DataSourceModel: any,
    private ResultModel: any,
    private ModeCollection: any,
    api: DeprecatedApiService,
    private favIconService: FavIconService
  ) {
    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.ManageDefinition),
        withLatestFrom(store.select('client', 'selectedDefinition', 'definition')),
        switchMap(([action, definition]) => {
          const model = new LibraryDefinitionModel.model(definition.resource);
          model.name = action.name;
          model.source = action.source;
          model.dataSourceId = action.dataSourceId;
          model.description = action.description;

          return this.updateDefinition(model, DefinitionUpdateStrategy.SaveOnly, {
            successCallback: action.modalConfirm,
          });
        })
      )
      .subscribe();

    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.UpdateDefinitionSuccess),
        withLatestFrom(this.datasourceFacade.datasources$)
      )
      .subscribe(([action, datasources]) => {
        const { data_source_id } = action.definition;
        const selected = datasources.find((ds) => ds.id === data_source_id);
        this.datasourceFacade.updateDefaultDatasource(selected.id);
        this.alertService.success('Updated definition');
      });

    actions$.pipe(ofType(SelectedDefinitionActionType.UpdateDefinitionFailed)).subscribe((action) => {
      alertService.error('Error saving definition');
      console.error(action.error);
    });

    actions$.pipe(ofType(SelectedDefinitionActionType.CancelPollDefinitionRun)).subscribe(() => {
      this.cancelling = true;
      $timeout.cancel(this.timeoutPromise);
    });

    this.actions$
      .pipe(
        ofType(SelectedDefinitionActionType.DeleteDefinition),
        withLatestFrom(store.select('client', 'selectedDefinition', 'definition')),
        switchMap(([action, definitionState]) =>
          this.destroyDefinition(definitionState.resource)
            .then(() => this.store.dispatch({ type: SelectedDefinitionActionType.DeleteDefinitionSuccess }))
            .catch((error) =>
              this.store.dispatch({ type: SelectedDefinitionActionType.DeleteDefinitionFailure, error })
            )
        )
      )
      .subscribe();

    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.DeleteDefinitionSuccess),
        withLatestFrom(store.select(reportContextSelector))
      )
      .subscribe(([action, reportContext]) => {
        this.alertService.success('Definition deleted successfully');
        this.$window.location.href = reportContext.links('home_web').href;
      });

    actions$.pipe(ofType(SelectedDefinitionActionType.DeleteDefinitionFailure)).subscribe((action) => {
      this.alertService.error('Error deleting definition');
      this.$log.error(action.error);
    });

    // This action fires when a user executes a report run
    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.ExecuteDefinitionRun),
        withLatestFrom(
          this.store.select('client', 'selectedDefinition', 'definition'),
          this.store.select('client', 'selectedDefinition', 'query')
        ),
        switchMap(([action, definition, query]) => {
          this.cancelling = false;
          const model = new LibraryDefinitionModel.model(definition.resource);
          model.source = action.source;

          return this.LibraryDefinitionModel.update(model).then(() => {
            model._resource
              .forms('execute')
              .$submit({
                source: model.source,
                limit: action.limit,
              })
              .then((res) => {
                $root.focused = false;
                this.currentRunToken = res.token;
                this.StoreService.dispatch(runDefinition());
                const run = new this.ReportRunModel.model(res);
                run
                  .queryRuns({ includes: { view: 'attrs' } })
                  .then(this.ModeCollection.elements)
                  .then((queryRuns) => {
                    if (queryRuns.length) {
                      this.store.dispatch({
                        type: SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess,
                        resource: queryRuns[0]._resource,
                      });
                    }
                  });
                return this.pollLoadedReportRun(run).then(() => (this.cancelling = false));
              })
              .catch((err) => {
                this.alertService.error('Error running definition');
                this.$log.error(err);
                this.StoreService.dispatch(runDefinitionFulfilled(false, true));
                this.store.dispatch({
                  type: SelectedDefinitionActionType.ExecuteDefinitionRunFailed,
                  error: err,
                });
              });
          });
        })
      )
      .subscribe();

    // This action should be fired when first loading a report page
    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.PollDefinitionRun),
        switchMap((action) =>
          api.getResource<QueryResource>(action.definitionModel._resource, 'query').then((query) => {
            this.store.dispatch({
              type: SelectedDefinitionActionType.LoadDefinitionQuerySuccess,
              resource: query,
            });

            return action.definitionModel
              .lastRun()
              .then((qr) => {
                this.StoreService.dispatch(runDefinition());

                this.store.dispatch({
                  type: SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess,
                  resource: qr._resource,
                });

                return qr.reportRun().then((rr) => {
                  this.currentRunToken = rr.token;
                  this.pollLoadedReportRun(rr);
                });
              })
              .catch(() => {});
          })
        )
      )
      .subscribe();

    // Cancel Query Run
    actions$
      .pipe(
        ofType(SelectedDefinitionActionType.CancelDefinitionQueryRun),
        withLatestFrom(store.select('client', 'selectedDefinition', 'queryRun')),
        switchMap(([action, queryRun]) => {
          this.cancelPollReportRun();

          return queryRun.resource
            .forms('cancel')
            .$submit()
            .then((res) => {
              this.store.dispatch({
                type: SelectedDefinitionActionType.CancelDefinitionQueryRunSuccess,
                resource: res,
              });
            })
            .catch((e) => {
              this.$log.warn('cancelling failed', e);
              this.store.dispatch({
                type: SelectedDefinitionActionType.CancelDefinitionQueryRunFailed,
                error: e,
              });
            });
        })
      )
      .subscribe();
  }

  private updateDefinition(model: any, strategy: DefinitionUpdateStrategyTypes, opts) {
    return this.LibraryDefinitionModel.update(model)
      .then((res) => {
        this.updateDefinitionSuccess(res._resource, opts.commitMessage);
        const callback = opts.successCallback;
        if (callback) {
          callback({ $definition: res });
        }
      })
      .catch((err) => {
        this.store.dispatch({
          type: SelectedDefinitionActionType.UpdateDefinitionFailed,
          error: err,
          token: model.token,
          strategy,
        });
      });
  }

  public updateDefinitionSuccess(definition: DefinitionResource, commitMessage: string = null) {
    this.store.dispatch({
      type: SelectedDefinitionActionType.UpdateDefinitionSuccess,
      strategy: DefinitionUpdateStrategy.SaveOnly,
      definition,
    });
  }

  public pollDefinitionRun(definition: any): void {
    // this.StoreService.dispatch(runDefinition());
    this.store.dispatch({
      type: SelectedDefinitionActionType.PollDefinitionRun,
      definitionModel: definition,
    });
  }

  public executeDefinitionRun(source: string, limit: boolean, selectedText?: string) {
    this.store.dispatch({
      type: SelectedDefinitionActionType.ExecuteDefinitionRun,
      source: source,
      limit: limit,
      selectedText: selectedText,
    });
  }

  public pollLoadedReportRun(reportRunModel: any): angular.IPromise<any> {
    const deferred = this.$q.defer<ReportRunResource>();
    const backoff = new Backoff({ min: minPollInterval, max: maxPollInterval, factor: pollFactor });

    this.store.dispatch({
      type: SelectedDefinitionActionType.DefinitionReportRunLoadSuccess,
      resource: reportRunModel._resource,
    });

    const poll = (rr: any) => {
      rr.queryRuns({ includes: { view: 'attrs' } })
        .then(this.ModeCollection.elements)
        .then((queryRuns: any[]) => {
          // Stop polling and just quit if another run has been requested
          if (this.currentRunToken !== rr.token) {
            return;
          }

          const completed = queryRuns.every((qr) => RUNNING_QUERY_STATES.indexOf(qr.state) === -1);
          queryRuns.forEach((qr) => {
            this.store.dispatch({
              type: SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess,
              resource: qr._resource,
            });
          });

          if (completed) {
            const resultPromises = queryRuns.map((qr) => this.loadQueryRunResult(qr._resource));

            this.StoreService.dispatch(runDefinitionFulfilled(false, false));

            this.store.dispatch({
              type: SelectedDefinitionActionType.PollDefinitionRunComplete,
              token: rr.token,
            });

            deferred.resolve(rr);
          } else {
            if (!this.cancelling) {
              this.timeoutPromise = this.$timeout(() => poll(rr), backoff.duration());
            }
          }
        })
        .catch((err) => {
          deferred.reject(err);
        });
    };

    poll(reportRunModel);

    return deferred.promise;
  }

  public cancelDefinitionRun() {
    this.store.dispatch({
      type: SelectedDefinitionActionType.CancelDefinitionQueryRun,
    });
  }

  public cancelPollReportRun() {
    this.store.dispatch({
      type: SelectedDefinitionActionType.CancelPollDefinitionRun,
    });
  }

  public manageDefinition(
    token: string,
    name: string,
    description: string,
    dataSourceId: string,
    source: string,
    modalConfirm: (definition) => void
  ) {
    this.store.dispatch({
      type: SelectedDefinitionActionType.ManageDefinition,
      name,
      token,
      description,
      dataSourceId,
      source,
      modalConfirm,
    });
  }

  public deleteSelectedDefinition() {
    this.store.dispatch({
      type: SelectedDefinitionActionType.DeleteDefinition,
    });
  }

  public updateDefinitionSource(source: string) {
    this.store.dispatch({
      type: SelectedDefinitionActionType.UpdateDefinitionSource,
      source,
    });
  }

  public get definitionRunState$() {
    return this.store.select('client', 'selectedDefinition', 'definition').pipe(
      combineLatest(
        this.store.select('client', 'selectedDefinition', 'query'),
        this.store.select('client', 'selectedDefinition', 'reportRun'),
        this.store.select('client', 'selectedDefinition', 'chartModel'),
        this.store.select('client', 'selectedDefinition', 'queryRun'),
        (definition, query, reportRun, chartModel, queryRun) => ({
          definition: definition,
          query: query,
          reportRun: reportRun,
          queryRun: queryRun,
          modelState: chartModel,
          dataset: chartModel && chartModel.model ? chartModel.model.engine.dataset : {},
        })
      )
    );
  }

  public get definitionPolling$() {
    return this.store.select('client', 'selectedDefinition', 'pollingDefinitionRun');
  }

  private loadQueryRunResult(queryRunResource: QueryRunResource) {
    const { query_token } = queryRunResource;

    if (queryRunResource.state === 'succeeded') {
      const qrModel = new this.QueryRunModel.model(queryRunResource);
      return qrModel.result().then((qrrModel) => {
        const queryRunResultResource = qrrModel._resource;
        // @todo will need w/y starts
        const view = QueryRunResourceHelpers.makeView(queryRunResource, queryRunResultResource);

        this.store.dispatch(new LoadQueryRunView(queryRunResource, queryRunResultResource, view));

        if (qrrModel.csv && !qrrModel.oversized) {
          return qrrModel.dataset().then((dataset) => {
            const { columns } = queryRunResultResource;
            const schema = Model.convertModeSchema(columns);
            const model = Model.build(schema, dataset.content);

            this.store.dispatchThunk(selectedDefinitionQueryResource, (queryResource) => ({
              type: SelectedDefinitionActionType.LoadDefinitionQueryResultSuccess,
              queryRun: queryRunResource,
              resource: qrrModel._resource,
              dataset,
              model,
              view,
              queryResource: queryResource,
            }));

            return dataset;
          });
        } else {
          this.store.dispatchThunk(selectedDefinitionQueryResource, (queryResource) => ({
            type: SelectedDefinitionActionType.LoadDefinitionQueryResultSuccess,
            queryRun: queryRunResource,
            resource: qrrModel._resource,
            queryResource: queryResource,
          }));

          return null;
        }
      });
    } else {
      this.store.dispatch({
        type: SelectedDefinitionActionType.LoadDefinitionQueryResultFailure,
        queryRun: queryRunResource,
      });

      const dataset = this.buildFailedDataset(queryRunResource);
      return this.$q.resolve(dataset);
    }
  }

  private buildModelResult(dataset) {
    const result = {
      isLoaded: true,
      queryName: null,
      state: dataset.state,
      columns: dataset.columns,
      content: dataset.content,
      count: dataset.count,
      query_token: dataset.query_token,
      reportDataUrl: dataset.reportDataUrl,
      reportQueryUrl: dataset.reportQueryUrl,
      content_length: dataset.content_length,
      csv: dataset.csv,
      oversized: dataset.oversized,
      model: Model.build(Model.convertModeSchema([]), []),
    };

    if (!dataset.state) {
      result.state = 'succeeded';
      result.model = Model.build(Model.convertModeSchema(dataset.columns), dataset.content);
    }

    return result;
  }

  private destroyDefinition(definition: DefinitionResource): angular.IPromise<any> {
    return definition.forms('destroy').$submit();
  }

  private buildFailedDataset(queryRun: QueryRunResource) {
    return {
      query_token: queryRun.query_token,
      state: queryRun.state,
      reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : undefined,
      reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('query_web').href : undefined,
    };
  }
}
